/**
 * primitives.tsx — drawing the geometry the Engine resolved.
 *
 * Every primitive here is rendered exactly as it arrived. There is no place in
 * this file where a shape is computed, inferred or corrected: if a body looks
 * wrong, the projection is wrong, and the fix belongs in the Engine where the
 * shape is decided. That is what makes the boundary auditable — a reader can
 * confirm by inspection that the browser drew what it was given.
 *
 * Coordinates are millimetres throughout. The canvas applies one transform for
 * the viewport and one per symbol instance; stroke widths are millimetres too,
 * so line weights scale with the drawing exactly as DIN EN ISO 10628 intends.
 */

import type { JSX } from "react";
import type { PrimitiveStyle, SymbolPrimitive } from "../types/contract";

/** Fallback line weight, mm — the thin DIN width, used when none is authored. */
const DEFAULT_STROKE_MM = 0.25;

function styleProps(style: PrimitiveStyle | undefined, fallbackFill = "none"): Record<string, string | number> {
  return {
    stroke: style?.stroke ?? "currentColor",
    strokeWidth: style?.strokeWidth ?? DEFAULT_STROKE_MM,
    fill: style?.fill ?? fallbackFill,
    ...(style?.opacity !== undefined ? { opacity: style.opacity } : {}),
    ...(style?.dasharray ? { strokeDasharray: style.dasharray } : {}),
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
}

/**
 * An arc given as a bounding box plus angles, expressed as an SVG path.
 *
 * SVG has no arc element, and `A` takes an endpoint rather than a sweep, so the
 * conversion is unavoidable. Angles are measured the way the canonical format
 * measures them: degrees, clockwise, zero pointing east in screen coordinates.
 */
export function arcPath(arc: { x: number; y: number; width: number; height: number; startAngleDeg: number; sweepAngleDeg: number }): string {
  const rx = arc.width / 2;
  const ry = arc.height / 2;
  const cx = arc.x + rx;
  const cy = arc.y + ry;
  const start = (arc.startAngleDeg * Math.PI) / 180;
  const end = ((arc.startAngleDeg + arc.sweepAngleDeg) * Math.PI) / 180;
  const from = { x: cx + rx * Math.cos(start), y: cy + ry * Math.sin(start) };
  const to = { x: cx + rx * Math.cos(end), y: cy + ry * Math.sin(end) };
  const largeArc = Math.abs(arc.sweepAngleDeg) > 180 ? 1 : 0;
  const sweep = arc.sweepAngleDeg >= 0 ? 1 : 0;
  return `M ${round(from.x)} ${round(from.y)} A ${round(rx)} ${round(ry)} 0 ${largeArc} ${sweep} ${round(to.x)} ${round(to.y)}`;
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function pointsAttr(points: Array<[number, number]>): string {
  return points.map(([x, y]) => `${round(x)},${round(y)}`).join(" ");
}

export function renderPrimitive(primitive: SymbolPrimitive): JSX.Element | null {
  switch (primitive.kind) {
    case "line":
      return <line key={primitive.id} x1={primitive.x1} y1={primitive.y1} x2={primitive.x2} y2={primitive.y2} {...styleProps(primitive.style)} />;
    case "rect":
      return <rect key={primitive.id} x={primitive.x} y={primitive.y} width={primitive.width} height={primitive.height} {...styleProps(primitive.style)} />;
    case "roundRect":
      return (
        <rect
          key={primitive.id}
          x={primitive.x} y={primitive.y} width={primitive.width} height={primitive.height}
          rx={primitive.rx ?? 0} ry={primitive.ry ?? primitive.rx ?? 0}
          {...styleProps(primitive.style)}
        />
      );
    case "circle":
      return <circle key={primitive.id} cx={primitive.cx} cy={primitive.cy} r={primitive.r} {...styleProps(primitive.style)} />;
    case "ellipse":
      return <ellipse key={primitive.id} cx={primitive.cx} cy={primitive.cy} rx={primitive.rx} ry={primitive.ry} {...styleProps(primitive.style)} />;
    case "arc":
      return <path key={primitive.id} d={arcPath(primitive)} {...styleProps(primitive.style)} />;
    case "path":
      return <path key={primitive.id} d={primitive.d} {...styleProps(primitive.style)} />;
    case "polyline":
      return <polyline key={primitive.id} points={pointsAttr(primitive.points)} {...styleProps(primitive.style)} />;
    case "polygon":
      return <polygon key={primitive.id} points={pointsAttr(primitive.points)} {...styleProps(primitive.style)} />;
    case "text":
      return (
        <text
          key={primitive.id}
          x={primitive.x} y={primitive.y}
          fontSize={primitive.fontSize ?? 3}
          fontFamily={primitive.fontFamily ?? "'DIN 1451', Arial, Helvetica, sans-serif"}
          textAnchor={primitive.textAnchor ?? "start"}
          fill={primitive.fill ?? primitive.style?.fill ?? "currentColor"}
          stroke="none"
        >
          {primitive.text}
        </text>
      );
    case "group":
      return (
        <g
          key={primitive.id}
          transform={groupTransform(primitive)}
        >
          {primitive.children.map(renderPrimitive)}
        </g>
      );
    default:
      // An unknown primitive kind is a projection this client is too old to
      // draw. It is skipped, and the compatibility check is what stops a
      // client this far behind from being used at all.
      return null;
  }
}

function groupTransform(group: Extract<SymbolPrimitive, { kind: "group" }>): string | undefined {
  const parts: string[] = [];
  if (group.rotationDeg) parts.push(`rotate(${group.rotationDeg})`);
  if (group.scaleX !== undefined || group.scaleY !== undefined) parts.push(`scale(${group.scaleX ?? 1} ${group.scaleY ?? 1})`);
  return parts.length ? parts.join(" ") : undefined;
}

/** The instance transform: canonical origin, canonical rotation, canonical flips. */
export function instanceTransform(instance: { origin: { x: number; y: number }; rotation: number; flipX?: boolean; flipY?: boolean }): string {
  const parts = [`translate(${round(instance.origin.x)} ${round(instance.origin.y)})`];
  if (instance.rotation) parts.push(`rotate(${instance.rotation})`);
  if (instance.flipX || instance.flipY) parts.push(`scale(${instance.flipX ? -1 : 1} ${instance.flipY ? -1 : 1})`);
  return parts.join(" ");
}
